import type { Message } from '@tandemone/types'
import { useQuery } from '@tanstack/react-query'
import { ArrowDown, File, MoreVertical, Send } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { useUser } from '@/domains/auth'
import {
  type DocumentDownloadResult,
  type PreviewDocument,
  DocumentPreviewDialog
} from '@/domains/supplier-profile'
import { cn } from '@/lib/utils'

import { fetchConversation } from './api'
import { type PendingAttachment, AttachmentPicker } from './attachment-picker'
import { conversationDetailKey } from './keys'
import { useMarkConversationRead } from './use-mark-read'
import { useMessages } from './use-messages'
import { useSendMessage } from './use-send-message'
import { useTypingEmitter } from './use-typing-emitter'
import { useTypingPresence } from './use-typing-presence'
import { useUnsendMessage } from './use-unsend-message'

/** right pane of /messages. shows the participants header, the message
 * thread (oldest-to-newest, scrolled to bottom), and a composer at the
 * bottom. mounts a read-marker effect that fires once the panel becomes
 * visible (drops the unread badge for this conversation server-side).
 */
export function MessageThread({ conversationId }: { conversationId: string }) {
  const { user } = useUser()

  const detailQuery = useQuery({
    queryKey: conversationDetailKey(conversationId),
    queryFn: ({ signal }) => fetchConversation(conversationId, signal),
    enabled: !!conversationId,
    staleTime: 30_000
  })

  const messagesQuery = useMessages(conversationId)
  const markRead = useMarkConversationRead()
  const sendMessage = useSendMessage(conversationId)
  const unsendMessage = useUnsendMessage(conversationId)
  const typingUsers = useTypingPresence(conversationId)
  const { notifyTyping, notifyStopped } = useTypingEmitter(conversationId)

  /** fire-and-forget mark-read on mount / conversation change. server
   * broadcasts a read_receipt + an unread_changed back so the badge
   * updates without a refetch.
   */
  useEffect(() => {
    if (!conversationId) return
    markRead.mutate(conversationId)
    /** intentionally only on conversation change . the mutate callback is
     * stable and re-running it on every render would spam the api.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  /** flatten the infinite-query pages into one chronologically-ordered
   * list (oldest first for display). pages are newest-first from the api;
   * each page is newest-first; reverse the items within and reverse the
   * pages so the final array is oldest -> newest.
   */
  const messages = useMemo<Message[]>(() => {
    const pages = messagesQuery.data?.pages ?? []
    const items: Message[] = []
    for (const page of pages) items.push(...page.items)
    items.reverse()
    return items
  }, [messagesQuery.data])

  /** every attachment across every message in oldest-first order. powers
   * the preview dialog so the thumbnail strip surfaces all conversation
   * documents and the user can navigate between them with arrow keys
   * regardless of which message they opened from.
   */
  const allAttachments = useMemo(() => {
    const flat: Array<{
      attachment: Message['attachments'][number]
      createdAt: string
    }> = []
    for (const message of messages) {
      for (const attachment of message.attachments) {
        flat.push({ attachment, createdAt: message.createdAt })
      }
    }
    return flat
  }, [messages])

  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const previewDocs = useMemo<PreviewDocument[]>(
    () =>
      allAttachments.map(({ attachment, createdAt }) => ({
        id: attachment.id,
        fileName: attachment.fileName,
        contentType: attachment.contentType,
        category: 'other',
        createdAt
      })),
    [allAttachments]
  )
  /** server already returns signed urls inline (serializeMessages). the
   * dialog's fetchUrl just resolves from the in-memory list . no extra
   * round trip per open.
   */
  function fetchAttachmentUrl(
    attachmentId: string
  ): Promise<DocumentDownloadResult> {
    const found = allAttachments.find(
      ({ attachment }) => attachment.id === attachmentId
    )
    if (!found) return Promise.reject(new Error('Attachment not found'))
    return Promise.resolve({
      previewUrl: found.attachment.previewUrl,
      downloadUrl: found.attachment.downloadUrl,
      expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString()
    })
  }

  /** scroll stickiness via ResizeObserver, not effects-on-messages.length.
   * the problem with effect-based scrolling: useLayoutEffect runs before
   * images load, so scrollHeight is short and we land mid-thread once
   * images finish.
   *
   * instead: while isAtBottom is true (default on mount), *any* resize
   * of the inner messages container re-pins the viewport to the bottom.
   * that covers (a) first paint, (b) images loading, (c) new messages
   * arriving while the user is reading the latest. once the user scrolls
   * up, isAtBottom flips false and the observer leaves their position
   * alone . they get a "↓ N new" pill instead.
   *
   * own sends override the at-bottom state: when the user posts, we
   * always pull them to the bottom regardless of where they were.
   */
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [missedCount, setMissedCount] = useState(0)
  const seenMessageIdsRef = useRef<Set<string>>(new Set())
  const didInitialBatchRef = useRef(false)
  const isAtBottomRef = useRef(true)
  useEffect(() => {
    isAtBottomRef.current = isAtBottom
  }, [isAtBottom])

  const getViewport = useCallback((): HTMLDivElement | null => {
    return (
      scrollContainerRef.current?.querySelector<HTMLDivElement>(
        '[data-slot="scroll-area-viewport"]'
      ) ?? null
    )
  }, [])

  const pinToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const viewport = getViewport()
      if (!viewport) return
      viewport.scrollTo({ top: viewport.scrollHeight, behavior })
    },
    [getViewport]
  )

  /** repin on every size change while at-bottom. fires immediately on
   * mount with the initial content size, and again as images load or
   * new messages arrive. when isAtBottom is false, the observer is a
   * no-op so the user's scroll position is preserved.
   */
  useEffect(() => {
    const viewport = getViewport()
    const inner = viewport?.firstElementChild as HTMLElement | null
    if (!viewport || !inner) return
    const observer = new ResizeObserver(() => {
      if (isAtBottomRef.current) {
        viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'auto' })
      }
    })
    observer.observe(inner)
    return () => observer.disconnect()
  }, [getViewport])

  /** react to newly-seen messages.
   *   - first batch (initial load): just record ids; ResizeObserver
   *     handles the scroll.
   *   - own send after that: force scroll regardless of position.
   *   - foreign while scrolled up: increment missedCount; the pill
   *     appears.
   *   - foreign while at bottom: ResizeObserver follows; nothing to do.
   */
  useEffect(() => {
    const seen = seenMessageIdsRef.current
    const fresh = messages.filter(message => !seen.has(message.id))
    if (fresh.length === 0) return

    if (!didInitialBatchRef.current) {
      didInitialBatchRef.current = true
      for (const message of fresh) seen.add(message.id)
      return
    }

    /** scroll/unread tracking is keyed on the *user* (did i send/see this),
     * not the company: it's about this client's reading position, distinct
     * from the company-based bubble alignment above. a teammate's message is
     * still "foreign" here so it counts toward the new-message indicator.
     */
    const anyOwn = fresh.some(message => message.senderUserId === user?.id)
    if (anyOwn) {
      pinToBottom('smooth')
      setIsAtBottom(true)
      setMissedCount(0)
    } else if (!isAtBottomRef.current) {
      const foreignCount = fresh.filter(
        message => message.senderUserId !== user?.id
      ).length
      if (foreignCount > 0) {
        setMissedCount(count => count + foreignCount)
      }
    }

    for (const message of fresh) seen.add(message.id)
  }, [messages, user?.id, pinToBottom])

  /** track scroll position on the radix viewport. consider "at bottom"
   * within a 40px slop so layout jitter doesn't break stickiness.
   */
  useEffect(() => {
    const viewport = getViewport()
    if (!viewport) return
    function handleScroll(): void {
      if (!viewport) return
      const distance =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
      const atBottom = distance < 40
      setIsAtBottom(atBottom)
      if (atBottom) setMissedCount(0)
    }
    viewport.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      viewport.removeEventListener('scroll', handleScroll)
    }
  }, [getViewport])

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      pinToBottom(behavior)
      setIsAtBottom(true)
      setMissedCount(0)
    },
    [pinToBottom]
  )

  /** don't early-return for loading/error: the scrollContainerRef must be
   * attached on first mount so ResizeObserver can observe it. otherwise
   * the first visit to a thread mounts the skeleton (no ref), the
   * effect short-circuits, and the observer never attaches even after
   * data lands. second-visit "works" purely because tanstack-query
   * cached the detail so the main JSX renders synchronously.
   */
  const detail = detailQuery.data
  /** a conversation is between companies, so ownership/alignment is company-
   * scoped: a message is "own" (right-aligned) when its sender company is the
   * one the caller acts as, regardless of which teammate sent it. it comes off
   * the response rather than setup state because a super acts as the TandemOne
   * Support tenant while their setup state carries no company at all, which
   * left every message in a super's thread reading as someone else's.
   */
  const activeCompanyId = detail?.viewerCompanyId ?? null
  const participantNames = detail
    ? detail.participants
        .map(participant => participant.companyName)
        .filter((name, idx, arr) => arr.indexOf(name) === idx)
        .join(' · ')
    : ''
  const hasError = detailQuery.isError || (!detailQuery.isLoading && !detail)

  return (
    <div className='flex h-full w-full flex-col'>
      <header className='flex flex-col gap-1 border-b border-border bg-card px-3 py-3 sm:px-6 sm:py-4'>
        {detail ? (
          <>
            <h2 className='font-display text-[18px] leading-tight break-words text-ink sm:text-[20px]'>
              {detail.subject}
            </h2>
            <p className='text-[12px] text-ink-muted'>{participantNames}</p>
          </>
        ) : (
          <>
            <Skeleton className='h-6 w-40' />
            <Skeleton className='h-4 w-64' />
          </>
        )}
      </header>

      <div
        ref={scrollContainerRef}
        className='relative flex min-h-0 flex-1 flex-col'
      >
        <ScrollArea className='min-h-0 flex-1'>
          <div className='flex flex-col gap-3 px-3 py-4 sm:px-6'>
            {hasError ? (
              <div className='rounded-md border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive'>
                Couldn&apos;t load this conversation.
              </div>
            ) : !detail || messagesQuery.isLoading ? (
              <>
                <Skeleton className='h-12 w-full' />
                <Skeleton className='h-12 w-3/4' />
              </>
            ) : messages.length === 0 ? (
              <p className='text-center text-[13px] text-ink-muted'>
                No messages yet.
              </p>
            ) : (
              messages.map(message => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.senderCompanyId === activeCompanyId}
                  onOpenAttachment={attachmentId => {
                    const globalIndex = allAttachments.findIndex(
                      ({ attachment }) => attachment.id === attachmentId
                    )
                    if (globalIndex >= 0) setPreviewIndex(globalIndex)
                  }}
                  onUnsend={() => unsendMessage.mutate(message.id)}
                />
              ))
            )}
          </div>
        </ScrollArea>
        {missedCount > 0 && !isAtBottom ? (
          <button
            type='button'
            onClick={() => scrollToBottom('smooth')}
            className='absolute bottom-3 left-1/2 inline-flex min-h-11 -translate-x-1/2 cursor-pointer items-center gap-1.5 rounded-full bg-accent px-4 text-[12px] font-medium text-white shadow-md hover:bg-accent/90 sm:min-h-0 sm:px-3 sm:py-1.5'
          >
            <ArrowDown className='size-3.5' />
            {missedCount === 1
              ? '1 new message'
              : `${missedCount} new messages`}
          </button>
        ) : null}
      </div>

      <DocumentPreviewDialog
        documents={previewDocs}
        initialIndex={previewIndex}
        onClose={() => setPreviewIndex(null)}
        fetchUrl={fetchAttachmentUrl}
      />

      <TypingIndicator users={typingUsers} />

      <Separator />

      <Composer
        sending={sendMessage.isPending}
        onTyping={notifyTyping}
        onSend={(body, attachments) => {
          notifyStopped()
          sendMessage.mutate({
            body,
            clientId: crypto.randomUUID(),
            attachments,
            senderUserId: user?.id ?? '',
            senderUserName: user?.name ?? null,
            /** stamp the active company so the optimistic row aligns right
             * (own) immediately. the server's real row carries the same
             * active company (with name) and replaces it on response.
             */
            senderCompanyId: activeCompanyId,
            senderCompanyName:
              detail?.participants.find(
                participant => participant.companyId === activeCompanyId
              )?.companyName ?? null
          })
        }}
      />
    </div>
  )
}

function MessageBubble({
  message,
  isOwn,
  onOpenAttachment,
  onUnsend
}: {
  message: Message
  isOwn: boolean
  onOpenAttachment: (attachmentId: string) => void
  onUnsend: () => void
}) {
  const time = new Date(message.createdAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  })
  const displayName =
    message.senderUserName ?? message.senderCompanyName ?? 'TandemOne'
  const isUnsent = message.deletedAt !== null

  if (isUnsent) {
    return (
      <div
        className={cn(
          'flex flex-col gap-1',
          isOwn ? 'items-end self-end' : 'items-start self-start'
        )}
      >
        <div className='text-[11px] break-words text-ink-muted'>
          <span className='font-medium text-ink-soft'>{displayName}</span>
          {` · ${time}`}
        </div>
        <div className='max-w-[85%] rounded-2xl border border-dashed border-border px-3.5 py-2 text-[12px] break-words italic text-ink-muted sm:max-w-[78%]'>
          This message was unsent by {message.deletedByUserName ?? displayName}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'group/bubble flex max-w-[85%] min-w-0 flex-col gap-1 sm:max-w-[78%]',
        isOwn ? 'items-end self-end' : 'items-start self-start'
      )}
    >
      <div className='text-[11px] break-words text-ink-muted'>
        <span className='font-medium text-ink-soft'>{displayName}</span>
        {/* a system message has no author, so displayName is already the
            company. don't print it twice. */}
        {message.senderCompanyName && message.senderCompanyName !== displayName
          ? ` · ${message.senderCompanyName}`
          : ''}
        {` · ${time}`}
      </div>
      <div
        className={cn(
          'flex min-w-0 items-center gap-1',
          isOwn ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        {message.body ? (
          <div
            className={cn(
              'tiptap-rendered min-w-0 rounded-2xl px-3.5 py-2 break-words',
              isOwn
                ? 'rounded-tr-sm bg-accent text-white [&_a]:text-white'
                : 'rounded-tl-sm bg-cream/70 text-ink'
            )}
            dangerouslySetInnerHTML={{ __html: message.body }}
          />
        ) : null}
        {isOwn ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon-sm'
                className='transition-opacity sm:opacity-0 sm:group-hover/bubble:opacity-100'
                aria-label='Message actions'
              >
                <MoreVertical className='size-3.5' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={isOwn ? 'end' : 'start'}
              className='min-w-32'
            >
              <DropdownMenuItem
                onSelect={onUnsend}
                className='text-rose-700 focus:text-rose-700'
              >
                Unsend
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {message.attachments.length > 0 ? (
        <ul className='flex flex-col gap-1.5'>
          {message.attachments.map(attachment => (
            <AttachmentItem
              key={attachment.id}
              attachment={attachment}
              isOwn={isOwn}
              onOpen={() => onOpenAttachment(attachment.id)}
            />
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function AttachmentItem({
  attachment,
  isOwn,
  onOpen
}: {
  attachment: Message['attachments'][number]
  isOwn: boolean
  onOpen: () => void
}) {
  const isImage = attachment.contentType.startsWith('image/')
  if (isImage) {
    return (
      <li>
        <button
          type='button'
          onClick={onOpen}
          className='block max-w-xs cursor-pointer overflow-hidden rounded-lg border border-border'
          title={attachment.fileName}
        >
          <img
            src={attachment.previewUrl}
            alt={attachment.fileName}
            className='h-auto w-full object-cover'
          />
        </button>
      </li>
    )
  }
  return (
    <li>
      <button
        type='button'
        onClick={onOpen}
        className={cn(
          /** the chip renders outside the bubble on the page background, so
           * own-message chips can't use the bubble's white-on-accent
           * scheme (it'd be invisible). tint them with the brand accent
           * instead, readable on the light surface.
           */
          'flex w-full cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-left text-[12px] transition-colors',
          isOwn
            ? 'border-accent/25 bg-accent/10 text-accent hover:bg-accent/15 dark:text-accent-mid'
            : 'border-border bg-card text-ink hover:bg-cream/40'
        )}
      >
        <File className='size-4 shrink-0' />
        <span className='flex min-w-0 flex-col'>
          <span className='truncate font-medium'>{attachment.fileName}</span>
          <span className='text-[10px] text-ink-muted'>
            {formatBytes(attachment.byteSize)}
          </span>
        </span>
      </button>
    </li>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function Composer({
  sending,
  onSend,
  onTyping
}: {
  sending: boolean
  onSend: (
    body: string,
    attachments: Array<{
      attachmentId: string
      fileName: string
      contentType: string
      byteSize: number
    }>
  ) => void
  onTyping: () => void
}) {
  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])

  const ready = attachments.every(attachment => attachment.status === 'ready')
  /** sending requires text. an attachment-only message would feel like
   * an accidental send when enter mis-fires from the file picker, so we
   * force a non-empty body. matches the user's expectation.
   */
  const hasText = draft.trim() !== ''
  const canSend = hasText && ready && !sending

  function submit() {
    if (!canSend) return
    const refs = attachments
      .filter(attachment => attachment.status === 'ready')
      .map(attachment => ({
        attachmentId: attachment.attachmentId,
        fileName: attachment.fileName,
        contentType: attachment.contentType,
        byteSize: attachment.byteSize
      }))
    onSend(draft, refs)
    setDraft('')
    setAttachments([])
  }

  return (
    <form
      onSubmit={event => {
        event.preventDefault()
        submit()
      }}
      className='flex flex-col gap-2 border-t border-border bg-card p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
    >
      <div className='flex items-end gap-2'>
        <RichTextEditor
          value={draft}
          onChange={next => {
            setDraft(next)
            if (next.trim() !== '') onTyping()
          }}
          onSubmit={submit}
          placeholder='Type a message…'
          className='flex-1'
        />
        <Button type='submit' size='icon-lg' disabled={!canSend}>
          <Send className='size-4' />
        </Button>
      </div>
      <AttachmentPicker
        attachments={attachments}
        onChange={setAttachments}
        disabled={sending}
      />
    </form>
  )
}

/** "X is typing…" strip pinned just above the composer. renders nothing
 * when no one is typing so the layout doesn't jump. names are joined
 * with " and " for 2, " and N others" for 3+.
 */
function TypingIndicator({
  users
}: {
  users: Array<{ userId: string; userName: string | null }>
}) {
  if (users.length === 0) return null
  const label = formatTypingLabel(users)
  return (
    <div className='flex items-center gap-1 border-t border-border bg-card px-3 py-1.5 text-[12px] text-ink-muted sm:px-6'>
      <span className='inline-flex gap-0.5'>
        <span className='size-1 animate-bounce rounded-full bg-ink-muted [animation-delay:-0.3s]' />
        <span className='size-1 animate-bounce rounded-full bg-ink-muted [animation-delay:-0.15s]' />
        <span className='size-1 animate-bounce rounded-full bg-ink-muted' />
      </span>
      <span>{label}</span>
    </div>
  )
}

function formatTypingLabel(
  users: Array<{ userId: string; userName: string | null }>
): string {
  const names = users.map(user => user.userName ?? 'Someone')
  if (names.length === 1) return `${names[0]} is typing…`
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`
  return `${names[0]} and ${names.length - 1} others are typing…`
}
